<script setup>
import { computed, ref, watch, nextTick, h } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ContextMenu from '@imengyu/vue3-context-menu';
import draggable from 'vuedraggable';
import markdownit from 'markdown-it';
import rubyPlugin from 'markdown-it-ruby';

import NameService from '../../services/NameService';
import RoleService from '../../services/RoleService';
import Staff from './Staff.vue';
import ExpandableFadeContent from '../ExpandableFadeContent.vue';

// Props
const props = defineProps({
    role: {
        type: Object,
        default: () => {},
    },
    category: {
        type: Object,
        default: () => {},
    }
});

// Emits
const emit = defineEmits([
    'delete-role',
    'duplicate-role'
]);

// Store
const store = useStore();

// i18n
const { t } = useI18n();

// Template refs
const japanese = ref(null);
const bulk = ref(null);

// Reactive data
const textFormatting = ref(false);
const controlOnStart = ref(true);
const text = ref("");
const showDescription = ref(false);

// Markdown setup
const md = markdownit({ linkify: true }).disable(['image']).use(rubyPlugin);

md.renderer.rules.heading_open = function (tokens, idx, options, env, self) {
    const token = tokens[idx];
    
    if (token.tag === 'h1') {
        return `<p class="text-xl font-rubik font-bold">`;
    } else if (token.tag === 'h2') {
        return `<p class="text-lg font-rubik font-bold">`;
    } else if (token.tag === 'h3') {
        return `<p class="text-md font-rubik font-bold">`;
    } else if (token.tag === 'h4') {
        return `<p class="text-base font-rubik font-bold">`;
    } else if (token.tag === 'h5') {
        return `<p class="text-sm font-rubik font-bold">`;
    } else if (token.tag === 'h6') {
        return `<p class="text-xs font-rubik font-bold">`;
    }
    return self.renderToken(tokens, idx, options);
};

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    
    if (!token.attrGet('href').startsWith('#')) {
        token.attrPush(['target', '_blank']);
    }
    return self.renderToken(tokens, idx, options);
};

// Computed properties
const staffList = computed(() => store.state.staffList);
const selectedEpisode = computed(() => store.state.selectedEpisode);
const settings = computed(() => store.state.staffList.settings);
const editMode = computed(() => store.state.editMode);

const note = computed(() => {
    if (!props.role || !props.role.note) return;
    return md.render(props.role.note);
});

const roleDescription = computed(() => {
    if (!props.role.original || typeof preloadGlossary === "undefined") {
        return null;
    }

    const role = preloadGlossary.find(t => t.ja === props.role.original);
    if (role) {
        return role.description;
    }

    return null;
});

// Watchers
watch(() => props.role, (val, oldVal) => {
    // Don't change the episode when bulk editing!
    if (textFormatting.value && oldVal && val && val.staff != oldVal.staff) {
        textFormatting.value = false;
    }
}, { immediate: true });

// Methods
const clone = (item) => {
    return JSON.parse(JSON.stringify(item));
};

const pullFunction = () => {
    return controlOnStart.value ? "clone" : true;
};

const start = ({ originalEvent }) => {
    controlOnStart.value = originalEvent.ctrlKey;
};

const checkShortcuts = (event, element) => {
    // Move the role up or down
    if (event.altKey && !event.shiftKey && !event.ctrlKey && (event.key == "ArrowDown" || event.key == "ArrowUp")) {
        event.preventDefault();
        const index = props.category.roles.indexOf(element);

        if (event.key == "ArrowUp") {
            if (index > 0) {
                props.category.roles.splice(index - 1, 0, props.category.roles.splice(index, 1)[0]);
            }

            // New focus
            nextTick(() => {
                if (!document.querySelector('.role-staff')?.parentNode?.previousElementSibling) {
                    return;
                }

                const newInputCol = japanese.value === event.target ? 0 : 1;
                const newInput = document.querySelector('.role-staff')?.parentNode?.previousElementSibling?.querySelectorAll('input')[newInputCol];

                if (newInput) {
                    newInput.focus();
                }
            });
        } else if (event.key == "ArrowDown") {
            if (index < props.category.roles.length - 1) {
                props.category.roles.splice(index + 1, 0, props.category.roles.splice(index, 1)[0]);
            }

            // New focus
            nextTick(() => {
                if (!document.querySelector('.role-staff')?.parentNode?.nextElementSibling) {
                    return;
                }

                const newInputCol = japanese.value === event.target ? 0 : 1;
                const newInput = document.querySelector('.role-staff')?.parentNode?.nextElementSibling?.querySelectorAll('input')[newInputCol];

                if (newInput) {
                    newInput.focus();
                }
            });
        }

        return;
    }

    if (event.key === "Enter" && event.ctrlKey && !event.altKey && !event.shiftKey) {
        // New role below
        const index = props.category.roles.indexOf(element);
        props.category.roles.splice(index + 1, 0, {
            name: "New role",
            staff: [{ja: '', en: '', untranslated: true}]
        });
    }
};

const translateRole = () => {
    if (!props.role.original) {
        return;
    }

    // Normalize the original role name
    if (/[•·･]/g.test(props.role.original)) {
        props.role.original = props.role.original.replace(/[•·･]/g, "・");
    }

    // Remove any space around ・ or ／
    if (/\s*[・／]\s*/g.test(props.role.original)) {
        props.role.original = props.role.original.replace(/\s*[・／]\s*/g, match => match.trim());
    }

    const roles = RoleService.translateRole(props.role.original, staffList.value, props.role);
    
    // Unit Director for OP/ED/...
    if (selectedEpisode.value.name.startsWith("OP") || selectedEpisode.value.name.startsWith("ED") || selectedEpisode.value.name.startsWith("Avan") || selectedEpisode.value.name.startsWith("Movie") || selectedEpisode.value.name.startsWith("MV")) {
        props.role.name = roles.replace(/Episode Director/g, "Unit Director");
        return;
    }

    props.role.name = roles;
};

const duplicateStaff = (element) => {
    const index = props.role.staff.indexOf(element);
    if (index == -1) {
        return;
    }

    props.role.staff.splice(index + 1, 0, {...element});
};

const moveStaff = (staff, to) => {
    if (to < 0) {
        return;
    }

    if (to >= props.role.staff.length) {
        to = props.role.staff.length - 1;
    }

    const from = props.role.staff.indexOf(staff);
    props.role.staff.splice(from, 1);
    props.role.staff.splice(to, 0, staff);
};

const addStaff = () => {
    props.role.staff.push({
        ja: "",
        en: "",
        untranslated: true
    });
};

const addStaffAt = (index) => {
    props.role.staff.splice(index, 0, {
        ja: "",
        en: "",
        untranslated: true
    });
};

const pasteStaffAt = (index, staff) => {
    for (let i = 0; i < staff.length; i++) {
        props.role.staff.splice(index + i, 0, {...staff[i]});
    }

    nextTick(() => {
        for (let i = 0; i < staff.length; i++) {
            NameService.addToTranslationQueue(props.role.staff[index + i], "lazy", undefined, props.category?.name);
        }
    });
};

const deleteStaff = (staff) => {
    const index = props.role.staff.indexOf(staff);
    if (index > -1) {
        // Clear JA/EN data to avoid useless translations in the queue
        delete staff.id;
        delete staff.pn;
        staff.ja = "";
        staff.en = "";

        props.role.staff.splice(index, 1);
    }
};

const deleteRoleStaff = () => {
    store.commit('addConfirm', {
        id: "clear-category",
        text: t('roleStaff.messages.clearStaffConfirm', { roleName: props.role.name }),
        callback: (result) => {
            if (!result) return;

            // Clear JA/EN data to avoid useless translations in the queue
            for (const staff of props.role.staff) {
                delete staff.id;
                delete staff.pn;
                staff.ja = "";
                staff.en = "";
            }
            props.role.staff = [];
        }
    });
};

const deleteRole = () => {
    store.commit('addConfirm', {
        id: "delete-role",
        text: t('roleStaff.messages.deleteRoleConfirm', { roleName: props.role.name }),
        callback: (result) => {
            if (!result) return;

            emit('delete-role', props.role);
        }
    });
};
const batchRenameJa = () => {
    if (!props.role.original && !props.role.name) {
        store.commit('addAlert', {
            id: "batch-rename-ja",
            title: "Error",
            text: t('roleStaff.messages.enterRoleNameFirst')
        });

        return;
    }

    if (!props.role.original) {
        store.commit('addPrompt', {
            id: "batch-rename-ja",
            title: "Batch native name correction",
            text: t('roleStaff.messages.enterNewJapaneseNameEn', { roleName: props.role.name }),
            type: "input",
            maxlength: 100,
            placeholder: "原画",
            callback: (newName) => {
                if (!newName) {
                    return;
                }

                const englishName = props.role.name;

                for (const episode of staffList.value.menus) {
                    for (const category of episode.credits) {
                        for (const role of category.roles) {
                            if (role.name === englishName) {
                                role.original = newName;
                            }
                        }
                    }
                }
            }
        });

        return;
    }

    store.commit('addPrompt', {
        id: "batch-rename-ja",
        title: "Batch native name correction",
        text: t('roleStaff.messages.enterNewJapaneseNameJa', { roleName: props.role.original }),
        type: "input",
        maxlength: 100,
        placeholder: "原画",
        value: props.role.original,
        callback: (newName) => {
            if (!newName) {
                return;
            }

            const oldName = props.role.original;

            for (const episode of staffList.value.menus) {
                for (const category of episode.credits) {
                    for (const role of category.roles) {
                        if (role.original === oldName) {
                            role.original = newName;
                        }
                    }
                }
            }
        }
    });
};
const batchRenameEn = () => {
    if (!props.role.original && !props.role.name) {
        store.commit('addAlert', {
            id: "batch-rename-en",
            title: "Error",
            text: t('roleStaff.messages.enterRoleNameFirst')
        });

        return;
    }

    if (!props.role.name) {
        store.commit('addPrompt', {
            id: "batch-rename-en",
            title: "Batch English name correction",
            text: t('roleStaff.messages.enterNewEnglishNameJa', { roleName: props.role.original }),
            type: "input",
            maxlength: 100,
            placeholder: "Key Animation",
            callback: (newName) => {
                if (!newName) {
                    return;
                }

                const japaneseName = props.role.original;

                for (const episode of staffList.value.menus) {
                    for (const category of episode.credits) {
                        for (const role of category.roles) {
                            if (role.original === japaneseName) {
                                role.name = newName;
                            }
                        }
                    }
                }
            }
        });
        
        return;
    }

    store.commit('addPrompt', {
        id: "batch-rename-en",
        title: "Batch English name correction",
        text: t('roleStaff.messages.enterNewEnglishNameEn', { roleName: props.role.name }),
        type: "input",
        maxlength: 100,
        placeholder: "Key Animation",
        value: props.role.name,
        callback: (newName) => {
            if (!newName) {
                return;
            }

            const oldName = props.role.name;

            for (const episode of staffList.value.menus) {
                for (const category of episode.credits) {
                    for (const role of category.roles) {
                        if (role.name === oldName) {
                            role.name = newName;
                        }
                    }
                }
            }
        }
    });
};
const bulkEdit = () => {
    if (textFormatting.value) {
        const splitRegex = new RegExp(`[${settings.value.delimiters}]+[ ]*[${settings.value.delimiters}]*`);

        let textValue = NameService.fixGreekLetters(text.value).replace(/\n[ 　]*\n/g, "\n;\n").split(splitRegex);
        let newStaff = [];
        for (let line of textValue) {
            let names = line.split(";");
            let person = {
                ja: (names[0] || "").trim(),
                en: (names[1] || "").trim(),
                untranslated: true
            }

            // Full romaji (ex: studio names, non-Japanese)
            if (person.ja && !person.en && /^[A-Za-zÀ-ÖØ-öø-ÿĀ-žƀ-ɏḀ-ỿ0-9 .,:;!?()\[\]{}'"’‘“”\/\\_\-°•・･℃@＠#$☆△▲%&*+=^|]+$/g.test(person.ja)) {
                // console.log("Switch", person.ja, person.en);
                [person.ja, person.en] = [person.en, person.ja];
            }

            newStaff.push(person);
        }
        // Clear JA/EN data to avoid useless translations in the queue
        for (const staff of props.role.staff) {
            delete staff.id;
            delete staff.pn;
            staff.ja = "";
            staff.en = "";
        }
        props.role.staff = newStaff;

        // Translate everyone
        for (const staff of props.role.staff) {
            if (staff.ja || staff.en) {
                NameService.addToTranslationQueue(staff, "lazy", undefined, props.category?.name);
            }
        }
    } else {
        let textValue = "";
        for (const staff of props.role.staff) {
            textValue += `${staff.ja||''};${staff.en||''}\n`;
        }
        text.value = textValue.trim();

        // Reset 1 default row
        if (text.value == ";") {
            text.value = "";
        }

        // Focus
        setTimeout(() => {
            if (bulk.value) {
                bulk.value.focus();
            }
        });
    }
    textFormatting.value = !textFormatting.value;
};

const cancelBulkEdit = () => {
    textFormatting.value = false;
};

/**
 * This function is used to reorder staff in a role by switching from a row-based order to a column-based order or the opposite.
 */
// const staffReorder = () => {
//     store.commit('addPrompt', {
//         id: "staff-reorder",
//         title: "Staff Reorder",
//         text: "Please enter the number of columns of the credits. For example, it is **3** columns in this example:\n\n```\n原画\nName1    Name2    Name3\nName4    Name5    Name6\n```\n\nMake sure that there is no row taking the full width, otherwise the result may be unexpected.",
//         type: "input",
//         maxlength: 3,
//         placeholder: "3",
//         callback: (data) => {
//             const columns = parseInt(data);

//             if (isNaN(columns)) {
//                 setTimeout(() => {
//                     store.commit('addAlert', {
//                         id: "staff-reorder",
//                         text: "Incorrect number. Please retry."
//                     });
//                 });
                
//                 return;
//             }

//             // Separating the staff into groups, using empty rows as separators.
//             let groups = [];
//             let current = [];
//             for (const staff of props.role.staff) {
//                 if (staff.ja == "" && staff.en == "") {
//                     if (current.length > 0) {
//                         groups.push(current);
//                     }
//                     current = [];
//                 } else {
//                     current.push(staff);
//                 }
//             }
//             if (current.length > 0) {
//                 groups.push(current);
//             }

//             const newStaff = [];
//             for (let group of groups) {
//                 if (newStaff.length > 0) {
//                     newStaff.push({
//                         ja: "",
//                         en: ""
//                     });
//                 }

//                 // console.log(group);
//                 let pushes = 0;
//                 let overflowColumns = group.length % columns;
//                 let minY = Math.floor(group.length / columns);
//                 let maxY = Math.ceil(group.length / columns);
//                 // console.log("overflowColumns", overflowColumns);

//                 for (let y = 0; y < maxY; y++) {
//                     // console.log("Y:", y);

//                     let overflow = overflowColumns;
//                     let currentOverflow = 0;
//                     for (let x = 0; x < columns; x++) {
//                         if (x > 0) {
//                             currentOverflow = Math.min(x, overflow);
//                         }
//                         let index = x * (minY) + y + currentOverflow;
//                         // console.log(group[index],"x:",x,"y:",y,"index:",index,"overflow:",overflow,"currentOverflow:",currentOverflow);
//                         newStaff.push(group[index]);
//                         pushes ++;
//                         if (pushes == group.length) {
//                             break;
//                         }
//                     }
//                 }
//             }

//             props.role.staff = newStaff;
//         }
//     });
// };

const handleClick = (e, item) => {
    const coords = e.currentTarget.getBoundingClientRect();

    ContextMenu.showContextMenu({
        theme: store.state.resolvedTheme,
        x: e.x !== 0 ? e.x : coords.x,
        y: e.y !== 0 ? e.y : coords.y + coords.height,
        zIndex: 1000,
        items: [
            {
                label: t('roleStaff.contextMenu.bulkEdit'),
                icon: h(FontAwesomeIcon, {
                    class: 'mx-fa-icon',
                    icon: 'fa-solid fa-file-import'
                }),
                onClick: () => {
                    bulkEdit();
                }
            },
            {
                label: t('common.translate'),
                icon: h(FontAwesomeIcon, {
                    class: 'mx-fa-icon',
                    icon: 'fa-solid fa-globe'
                }),
                onClick: () => {
                    for (const staff of props.role.staff) {
                        if (staff.ja || staff.en) {
                            NameService.addToTranslationQueue(staff, "lazy", undefined, props.category?.name);
                        }
                    }
                }
            },
            {
                label: t('roleStaff.contextMenu.batchRenameJa'),
                icon: h(FontAwesomeIcon, {
                    class: 'mx-fa-icon',
                    icon: 'fa-solid fa-link',
                    transform: 'shrink-2'
                }),
                onClick: () => {
                    batchRenameJa();
                }
            },
            {
                label: t('roleStaff.contextMenu.batchRenameEn'),
                icon: h(FontAwesomeIcon, {
                    class: 'mx-fa-icon',
                    icon: 'fa-solid fa-link',
                    transform: 'shrink-2'
                }),
                onClick: () => {
                    batchRenameEn();
                }
            },
            {
                label: t('common.duplicate'),
                icon: h(FontAwesomeIcon, {
                    class: 'mx-fa-icon',
                    icon: 'fa-solid fa-copy'
                }),
                onClick: () => {
                    emit("duplicate-role", item);
                }
            },
            {
                label: t('roleStaff.contextMenu.copyRole'),
                divided: true,
                icon: h(FontAwesomeIcon, {
                    class: 'mx-fa-icon',
                    icon: 'fa-regular fa-copy'
                }),
                onClick: () => {
                    const data = JSON.stringify([props.role]);
                    navigator.clipboard.writeText(data);
                }
            },
            {
                label: t('roleStaff.contextMenu.addNote'),
                hidden: props.role.hasOwnProperty('note'),
                divided: true,
                icon: h(FontAwesomeIcon, {
                    class: 'mx-fa-icon',
                    icon: 'fa-solid fa-sticky-note'
                }),
                onClick: () => {
                    props.role.note = "";
                }
            },
            {
                label: t('roleStaff.contextMenu.deleteNote'),
                hidden: !props.role.hasOwnProperty('note'),
                divided: true,
                customClass: "important",
                icon: h(FontAwesomeIcon, {
                    class: 'mx-fa-icon',
                    icon: 'fa-solid fa-sticky-note'
                }),
                onClick: () => {
                    store.commit('addConfirm', {
                        id: "delete-note",
                        text: t('roleStaff.messages.deleteNoteConfirm', { roleName: props.role.name }),
                        callback: (result) => {
                            if (!result) return;

                            delete props.role.note;
                        }
                    });
                }
            },
            {
                label: t('roleStaff.contextMenu.convertToEpisodeRole'),
                hidden: selectedEpisode.value.name !== "Overview",
                onClick: () => {
                    for (const staff of props.role.staff) {
                        if (staff.comment) {
                            const comment = staff.comment.replace(/ep(isode)?s?/gi, "").replace(/#/g, "").trim();

                            if (!/^(\d+(\s*-\s*\d+)?(\s*,\s*)?)+$/.test(comment)) {
                                store.commit('addAlert', {
                                    id: "convert-to-episode-role",
                                    title: "Error",
                                    text: t('roleStaff.messages.invalidEpisodeFormat')
                                });

                                return;
                            }
                        }
                    }

                    const parseEpisodeString = (episodeString) => {
                        const episodes = [];
                        const ranges = episodeString.replace(/ep(isode)?s?/gi, "").replace(/#/g, "").split(',').map(item => item.trim());

                        ranges.forEach(range => {
                            if (range.includes('-')) {
                                const [start, end] = range.split('-').map(Number);
                                for (let i = start; i <= end; i++) {
                                    episodes.push(i);
                                }
                            } else {
                                episodes.push(Number(range));
                            }
                        });

                        return episodes;
                    }

                    const addToEpisode = (episode) => {
                        const numericEpisode = parseInt(episode.name.replace("#", ""));

                        for (const staff of props.role.staff) {
                            if (staff.comment && !parseEpisodeString(staff.comment).includes(numericEpisode)) {
                                continue;
                            }

                            let category = episode.credits.find(c => c.name === props.category.name);
                            if (!category) {
                                category = {
                                    ...props.category,
                                    roles: []
                                };
                                episode.credits.push(category);
                            }

                            let role = category.roles.find(r => (r.original && r.original === props.role.original) || (!r.original && !props.role.original && r.name === props.role.name));
                            if (!role) {
                                role = {
                                    ...props.role,
                                    staff: []
                                };
                                category.roles.push(role);
                            }

                            const newStaff = { ...staff };
                            delete newStaff.comment;
                            role.staff.push(newStaff);
                        }
                    };

                    for (const episode of staffList.value.menus) {
                        if (!episode.name.startsWith("#")) {
                            continue;
                        }

                        addToEpisode(episode);
                    }

                    emit('delete-role', props.role);
                }
            },
            {
                label: t('roleStaff.contextMenu.convertToOverviewRole'),
                hidden: selectedEpisode.value.name === "Overview",
                onClick: () => {
                    const runConvertion = () => {
                        const episodeAppearances = {};

                        const overview = staffList.value.menus.find(e => e.name === "Overview");
                        if (!overview) {
                            store.commit('addAlert', {
                                id: "convert-to-overview-role",
                                title: "Error",
                                text: t('roleStaff.messages.overviewEpisodeMissing')
                            });

                            return;
                        }

                        for (const episode of staffList.value.menus) {
                            if (episode.name.startsWith("#")) {
                                for (const category of episode.credits) {
                                    for (const role of category.roles) {
                                        if (role.original === props.role.original && role.name === props.role.name) {
                                            for (const staff of role.staff) {
                                                const studio = typeof staff.studio === 'object' ? (staff.studio.ja + '-' + staff.studio.en) : staff.studio;
                                                const pn = typeof staff.pn === 'object' ? (staff.pn.ja + '-' + staff.pn.en) : staff.pn;
                                                const hash = `${staff.id}-${staff.ja}-${staff.en}-${studio}-${pn}-${staff.comment}-${staff.isNC}-${staff.isMiscredit}-${staff.isInvisible}`;

                                                if (!episodeAppearances[hash]) {
                                                    episodeAppearances[hash] = {
                                                        staff: { ...staff },
                                                        episodes: [],
                                                    }
                                                }

                                                const numericEpisode = parseInt(episode.name.replace("#", ""));
                                                episodeAppearances[hash].episodes.push(numericEpisode);
                                            }

                                            // Remove the role from the episode
                                            const index = category.roles.indexOf(role);
                                            category.roles.splice(index, 1);

                                            // Remove the category if empty
                                            if (category.roles.length === 0) {
                                                const categoryIndex = episode.credits.indexOf(category);
                                                episode.credits.splice(categoryIndex, 1);
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        let category = overview.credits.find(c => c.name === props.category.name);
                        if (!category) {
                            category = {
                                ...props.category,
                                roles: []
                            };
                            overview.credits.push(category);
                        }

                        let role = category.roles.find(r => r.name === props.role.name);
                        if (!role) {
                            role = {
                                ...props.role,
                                staff: []
                            };
                            category.roles.push(role);
                        }

                        for (const [identifier, data] of Object.entries(episodeAppearances)) {
                            const newStaff = { ...data.staff };
                            const episodes = data.episodes.sort((a, b) => a - b);
                            const ranges = [];
                            let start = episodes[0];

                            for (let i = 1; i <= episodes.length; i++) {
                                if (i === episodes.length || episodes[i] !== episodes[i - 1] + 1) {
                                    if (start === episodes[i - 1]) {
                                        ranges.push(`${start}`);
                                    } else {
                                        ranges.push(`${start}-${episodes[i - 1]}`);
                                    }
                                    start = episodes[i];
                                }
                            }

                            const numberOfEpisodes = staffList.value.menus.filter(e => e.name.startsWith("#")).length;

                            if (episodes.length !== 0 && episodes.length !== numberOfEpisodes) {
                                const episodeLabel = episodes.length > 1 ? 'Episodes' : 'Episode';
                                newStaff.comment = `${episodeLabel} ${ranges.join(', ')}\n\n${newStaff.comment || ''}`.trim();
                            }

                            role.staff.push(newStaff);
                        }
                    };

                    store.commit('addConfirm', {
                        id: "convert-to-overview-role",
                        text: t('roleStaff.messages.convertToOverviewConfirm', { roleName: props.role.name }),
                        callback: (result) => {
                            if (!result) return;

                            runConvertion();
                        }
                    });
                }
            },
            {
                label: t('roleStaff.contextMenu.move'),
                children: [
                    {
                        label: t('common.moveToBeginning'),
                        icon: h(FontAwesomeIcon, {
                            class: 'mx-fa-icon',
                            icon: 'fa-solid fa-angle-up'
                        }),
                        onClick: () => {
                            const index = props.category.roles.indexOf(item);

                            if (index > 0) {
                                props.category.roles.splice(0, 0, props.category.roles.splice(index, 1)[0]);
                            }
                        }
                    },
                    {
                        label: t('roleStaff.contextMenu.moveUp'),
                        icon: h(FontAwesomeIcon, {
                            class: 'mx-fa-icon',
                            icon: 'fa-solid fa-angle-up'
                        }),
                        onClick: () => {
                            const index = props.category.roles.indexOf(item);

                            if (index > 0) {
                                props.category.roles.splice(index - 1, 0, props.category.roles.splice(index, 1)[0]);
                            }
                        }
                    },
                    {
                        label: t('roleStaff.contextMenu.moveDown'),
                        icon: h(FontAwesomeIcon, {
                            class: 'mx-fa-icon',
                            icon: 'fa-solid fa-angle-down'
                        }),
                        onClick: () => {
                            const index = props.category.roles.indexOf(item);

                            if (index < props.category.roles.length - 1) {
                                props.category.roles.splice(index + 1, 0, props.category.roles.splice(index, 1)[0]);
                            }
                        }
                    },
                    {
                        label: t('common.moveToEnd'),
                        divided: true,
                        icon: h(FontAwesomeIcon, {
                            class: 'mx-fa-icon',
                            icon: 'fa-solid fa-angle-down'
                        }),
                        onClick: () => {
                            const index = props.category.roles.indexOf(item);

                            if (index < props.category.roles.length - 1) {
                                props.category.roles.splice(props.category.roles.length, 0, props.category.roles.splice(index, 1)[0]);
                            }
                        }
                    },
                    {
                        label: t('roleStaff.contextMenu.moveToAnotherCategory'),
                        divided: store.state.selectedEpisode.name !== "Overview",
                        children: staffList.value.settings.categories.map(customCategory => ({
                            label: customCategory.name,
                            onClick: () => {
                                let category = store.state.selectedEpisode.credits.find(c => c.name === customCategory.name);
                                if (!category) {
                                    category = {
                                        name: customCategory.name,
                                        roles: []
                                    };
                                    store.state.selectedEpisode.credits.push(category);
                                }

                                const index = props.category.roles.indexOf(item);
                                props.category.roles.splice(index, 1);

                                category.roles.push(item);
                            }
                        }))
                    },
                    {
                        label: t('roleStaff.contextMenu.moveToOverview'),
                        hidden: selectedEpisode.value.name === "Overview",
                        onClick: () => {
                            let episode = staffList.value.menus.find(e => e.name === "Overview");
                            if (!episode) {
                                episode = {
                                    name: "Overview",
                                    credits: []
                                };
                                staffList.value.menus.unshift(episode);
                            }

                            let category = episode.credits.find(c => c.name === props.category.name);
                            if (!category) {
                                category = {
                                    name: props.category.name,
                                    roles: []
                                };
                                episode.credits.push(category);
                            }

                            category.roles.push(item);

                            const index = props.category.roles.indexOf(item);
                            props.category.roles.splice(index, 1);
                        }
                    }
                ]
            },
            {
                label: t('roleStaff.contextMenu.insert'),
                divided: true,
                children: [
                    {
                        label: t('roleStaff.contextMenu.insertRoleAbove'),
                        icon: h(FontAwesomeIcon, {
                            class: 'mx-fa-icon',
                            icon: 'fa-solid fa-angle-up'
                        }),
                        onClick: () => {
                            const index = props.category.roles.indexOf(item);
                            props.category.roles.splice(index, 0, {
                                name: t('categoryStaff.newRole'),
                                staff: [{ja: '', en: '', untranslated: true}]
                            });
                        }
                    },
                    {
                        label: t('roleStaff.contextMenu.insertRoleBelow'),
                        icon: h(FontAwesomeIcon, {
                            class: 'mx-fa-icon',
                            icon: 'fa-solid fa-angle-down'
                        }),
                        onClick: () => {
                            const index = props.category.roles.indexOf(item);
                            props.category.roles.splice(index + 1, 0, {
                                name: t('categoryStaff.newRole'),
                                staff: [{ja: '', en: '', untranslated: true}]
                            });
                        }
                    }
                ]
            },
            {
                label: t('roleStaff.contextMenu.clearStaff'),
                customClass: "important",
                icon: h(FontAwesomeIcon, {
                    class: 'mx-fa-icon',
                    icon: 'fa-solid fa-times'
                }),
                onClick: () => {
                    deleteRoleStaff();
                }
            },
            {
                label: t('common.delete'),
                customClass: "important",
                icon: h(FontAwesomeIcon, {
                    class: 'mx-fa-icon',
                    icon: 'fa-solid fa-trash'
                }),
                onClick: () => {
                    deleteRole();
                }
            }
        ]
    });
};
</script>

<template>
    <textarea
        v-if="editMode && role.hasOwnProperty('note')"
        v-model.trim="role.note"
        ref="noteRef"
        class="w-full bg-app-surface0 text-app-text rounded-t-md p-3 shadow outline-none focus:shadow-lg transition-shadow duration-100 -mb-2"
        :class="{'highlighted': role.isHighlighted}"
        :placeholder="$t('common.note')"
        rows="2"
        maxlength="2000"
    ></textarea>

    <ExpandableFadeContent
        v-else-if="!editMode && role.note"
        id="note-viewer"
        class="md-viewer text-sm"
        :class="{
            'rounded-md mb-4': !role.hasOwnProperty('note'),
            'rounded-t-md': role.hasOwnProperty('note'),
            'highlighted': role.isHighlighted
        }"
        container-class="relative overflow-hidden"
        content-tag="section"
        :watch-key="note"
        collapsed-height="12em"
    >
        <font-awesome-icon icon="fa-regular fa-note-sticky" class="float-right -mt-1 text-slate-200 dark:text-slate-800" size="2xl" />
        
        <span v-html="note"></span>
    </ExpandableFadeContent>

    <div class="bg-app-surface3 text-app-text-soft px-4 py-3 text-xs -translate-y-full absolute max-w-[400px] z-[800] rounded-md shadow" v-if="showDescription">
        {{ roleDescription }}
    </div>

    <table
        class="text-left overflow-hidden w-full shadow-sm role-staff"
        :class="{
            'highlighted': role.isHighlighted,
            'rounded-md': !role.hasOwnProperty('note'),
            'rounded-b-md': role.hasOwnProperty('note')
        }"
    >
        <thead>
            <tr>
                <th colspan="2" :class="{'w-full flex flex-row items-center gap-2': editMode}">
                    <i v-if="editMode && !textFormatting" class="mhandle cursor-grab active:cursor-grabbing">
                        <font-awesome-icon icon="fa-solid fa-bars" />
                    </i>
                    <div v-if="editMode" class="flex-1 flex flex-col gap-1">
                        <input
                            type="text"
                            ref="japanese"
                            :placeholder="$t('roleStaff.placeholders.originalRoleName')"
                            class="w-full bg-transparent border-[#ffffff20] outline-none px-1 border-2 rounded-md text-xs placeholder:text-[#ffffff20]"
                            v-model.trim="role.original"
                            @keydown="checkShortcuts($event, role)"
                            @input="translateRole"
                        />
                        <input
                            type="text"
                            ref="english"
                            :placeholder="$t('roleStaff.placeholders.translatedRoleName')"
                            class="w-full bg-transparent border-[#ffffff20] outline-none px-1 border-2 rounded-md placeholder:text-[#ffffff20]"
                            v-model.trim="role.name"
                            @keydown="checkShortcuts($event, role)"
                        />
                    </div>
                    <div v-else class="relative">
                        <p v-if="role.name && role.name != role.original" class="text-xs">{{ role.original }}</p>
                        <p>{{ role.name ? role.name : role.original }}</p>

                        <font-awesome-icon
                            v-if="roleDescription"
                            icon="fa-solid fa-circle-info"
                            class="absolute right-1 top-1 text-sm text-gray-white outline-none"
                            tabindex="0"
                            @focus="showDescription = true"
                            @blur="showDescription = false"
                            @mouseenter="showDescription = true"
                            @mouseleave="showDescription = false"
                        />
                    </div>
                    <div v-if="editMode" class="flex gap-1">
                        <button class="flex-center text-center w-6 h-6 hover:bg-white hover:bg-opacity-10 cursor-pointer rounded-full" @click="bulkEdit" title="Bulk Edit">
                            <font-awesome-icon icon="fa-solid fa-file-import" transform="shrink-2 left-1" />
                        </button>
                        <button class="flex-center text-center w-6 h-6 hover:bg-white hover:bg-opacity-10 cursor-pointer rounded-full" @click="addStaff" title="Add Staff">
                            <font-awesome-icon icon="fa-solid fa-add" />
                        </button>
                        <button class="flex-center text-center w-6 h-6 hover:bg-white hover:bg-opacity-10 cursor-pointer rounded-full" @click.prevent.stop="handleClick($event, role)" title="Details">
                            <font-awesome-icon icon="fa-solid fa-ellipsis" />
                        </button>
                    </div>
                </th>
            </tr>
        </thead>
        <template v-if="role.staff && Array.isArray(role.staff)">
            <button v-if="editMode && !textFormatting" class="btn-primary text-white text-center w-full cursor-pointer" @click="addStaffAt(0)" v-show="role.staff.length > 0">
                <font-awesome-icon icon="fa-solid fa-add" />
            </button>
            <draggable
                v-if="!textFormatting"
                v-model.trim="role.staff"
                :animation="50"
                :clone="clone"
                :group="{ name: 'staff', pull: pullFunction }"
                @start="start"
                item-key="dynid"
                handle=".handle"
                tag="tbody"
                class="text-center text-[#333333] dark:text-gray-300"
            >
                <template #item="{element, index}">
                    <Staff
                        :staff="element"
                        :position="index"
                        :category="props.category"
                        @add-staff-at="addStaffAt"
                        @paste-staff-at="pasteStaffAt"
                        @duplicate-staff="duplicateStaff"
                        @move-staff="moveStaff"
                        @delete-staff="deleteStaff"
                    />
                </template>
            </draggable>
            <tbody v-else>
                <div class="flex flex-col">
                    <textarea v-model.trim="text" ref="bulk" class="w-full h-[200px] outline-none bg-app-surface0 text-app-text py-2 px-3 text-[14px]" :placeholder="$t('roleStaff.placeholders.bulkEditStaff')"></textarea>
                    <div class="flex w-full text-sm text-center">
                        <button class="bg-crimson-400 hover:bg-crimson-500 focus:bg-crimson-500 text-white w-3/6 p-1 cursor-pointer outline-none" @click="cancelBulkEdit">
                            {{ $t('common.cancel') }}
                        </button>
                        <button class="bg-fern-400 hover:bg-fern-500 focus:bg-fern-500 text-white w-3/6 p-1 cursor-pointer outline-none" @click="bulkEdit">
                            {{ $t('common.save') }}
                        </button>
                    </div>
                </div>
            </tbody>
            <button v-if="editMode && !textFormatting" class="btn-primary text-white text-center w-full cursor-pointer" @click="addStaff">
                <font-awesome-icon icon="fa-solid fa-add" />
            </button>
        </template>
    </table>
</template>
